import { z } from 'zod';
import { contentIdSchema } from '../rules/content-id.js';
import { effectSchema } from '../rules/effect.js';
import { resourceSchema } from './resource.js';
/**
 * Общая часть записи контента (D7, D20).
 *
 * Главное разделение здесь — между **текстом** и **механикой**.
 * `text` — описание из источника: оно показывается пользователю
 * и в расчёте не участвует ни при каких условиях. Механика — это
 * `effects` (D5) и поля `mechanics`: числа, по которым считает движок.
 *
 * Разделение не косметическое: тексты предстоит переводить (D11)
 * и переписывать, и делать это нужно, не трогая механику. Если бы
 * число жило внутри фразы «+2 к КД», перевод ломал бы расчёт.
 */
/** Откуда взята запись: чтобы проверяющий нашёл место в документе. */
export const srdSourceSchema = z.strictObject({
    document: z.literal('SRD 5.2'),
    /** Раздел документа: «Equipment, Armor table», «Spell Descriptions». */
    section: z.string().min(1),
    /** Страница PDF — та, что напечатана в документе. */
    page: z.number().int().positive(),
});
/**
 * Запись, заведённая человеком через редактор (D7, D53).
 *
 * Ссылки на страницу у неё нет и быть не может, а сказать, откуда
 * взято, всё равно нужно: через год мастер не вспомнит, выдумал он
 * подкласс сам или переписал из книги. Поле свободное и необязательное —
 * это заметка, а не библиографическая ссылка.
 */
export const customSourceSchema = z.strictObject({
    document: z.literal('custom'),
    note: z.string().max(200).nullable(),
});
/**
 * Источник записи. Две формы, и различаются они по `document`:
 * запись SRD ссылается на страницу документа, пользовательская —
 * на заметку автора. Одна схема на обе потому, что дальше по коду
 * запись одна и та же: движок, каталог и лист про разницу не знают
 * (rules-engine.md, раздел 7.3).
 */
export const contentSourceSchema = z.discriminatedUnion('document', [
    srdSourceSchema,
    customSourceSchema,
]);
/**
 * Перевод описания (D32).
 *
 * Лежит рядом с оригиналом, а не вместо него: перевод — переработка
 * материала SRD, и лицензия требует, чтобы это было видно
 * (`ATTRIBUTION.md`). Читатель должен иметь возможность вернуться
 * к исходной строке, а проверяющий — сверить числа.
 *
 * `by` помечает, чей перевод. Пока значение одно: наш собственный.
 * Оно не украшение — выдавать переработку за оригинал лицензия
 * не разрешает.
 */
export const translatedTextSchema = z.strictObject({
    text: z.string().min(1),
    by: z.literal('rollcall'),
});
export const translationSchema = z.strictObject({
    ru: translatedTextSchema,
});
export const contentBaseShape = {
    id: contentIdSchema,
    name: z.string().min(1),
    source: contentSourceSchema,
    /** Описание из источника. В расчёт не попадает никогда. */
    text: z.string().min(1),
    /**
     * Перевод описания; `null` — записи ещё не переводили. Переводы
     * идут заходами по видам записей (D32), и незаконченный перевод
     * ничего не ломает: запись без него показывается по-английски.
     *
     * В расчёт не попадает, как и `text`: текст и механика разделены
     * ровно для того, чтобы перевод не мог сдвинуть ни одного числа.
     */
    translation: translationSchema.nullable().default(null),
    /** Механика в терминах D5. Пусто — значит, запись ничего не считает. */
    effects: z.array(effectSchema),
    /**
     * Счётчики ресурсов (D56): ограниченное число использований.
     *
     * Рядом с эффектами, но не эффект: эффект меняет вычисляемую
     * величину листа, а счётчик — состояние между отдыхами. Пусто
     * у подавляющего большинства записей, поэтому со значением
     * по умолчанию: старый JSON остаётся разбираемым.
     */
    resources: z.array(resourceSchema).default([]),
};
/** Именованная особенность с собственным текстом и механикой. */
export const namedFeatureSchema = z.strictObject({
    key: z.string().regex(/^[a-z0-9_]+$/),
    name: z.string().min(1),
    text: z.string().min(1),
    /**
     * Перевод описания особенности (D32). Ключ перевода — `<id>#<key>`:
     * у записи класса таких описаний под сотню, и одного ключа записи
     * на них не хватает.
     */
    translation: translationSchema.nullable().default(null),
    effects: z.array(effectSchema),
    /**
     * Счётчики самой особенности (D56). Здесь, а не только в записи,
     * потому что ресурс показывается там, где живёт его особенность:
     * ярость стоит под «Rage», а не общим списком в стороне.
     */
    resources: z.array(resourceSchema).default([]),
});
export const abilityNameSchema = z.enum([
    'strength',
    'dexterity',
    'constitution',
    'intelligence',
    'wisdom',
    'charisma',
]);
/**
 * Вид записи контента отдельной схемой.
 *
 * Список повторяет объединение `srdContentSchema`, и это сделано
 * намеренно: вывести его из объединения значило бы тянуть в клиент
 * схемы всех заклинаний и предметов ради одного поля «вид источника»
 * на листе персонажа (`apps/web/bundle-report.md`). Совпадение
 * списка с объединением проверяется на типах — см. `content/index.ts`.
 */
export const contentTypeSchema = z.enum([
    'spell',
    'item',
    'species',
    'class',
    'subclass',
    'background',
    'feat',
    'skill',
    'language',
    'rule',
    /** Состояние: «отравлен», «схвачен» (глоссарий, статьи `[Condition]`). */
    'condition',
    /** Монстр: статблок из «Monsters A–Z» и «Animals» (D50). */
    'monster',
]);
//# sourceMappingURL=common.js.map